import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/agencyPortal/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/agencyPortal/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/agencyPortal/FETCH_FAILURE'
const CREATE_REQUEST = 'realhub/agencyPortal/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/agencyPortal/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/agencyPortal/CREATE_FAILURE'
const UPDATE_REQUEST = 'realhub/agencyPortal/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/agencyPortal/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/agencyPortal/UPDATE_FAILURE'
const DELETE_REQUEST = 'realhub/agencyPortal/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/agencyPortal/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/agencyPortal/DELETE_FAILURE'

// Initial State
const initialState = {
  loading: false,
  loaded: false,
  result: [],
  creating: false,
  updating: false,
  deleting: false,
  errors: [],
}

// Actions
export function fetchRequest(){
  return {
    type: FETCH_REQUEST,
  }
}

export function fetchSuccess(payload = {}){
  return {
    type: FETCH_SUCCESS,
    result: payload.result,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(payload = {}){
  return {
    type: CREATE_SUCCESS,
    result: payload.result,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(payload = {}){
  return {
    type: DELETE_SUCCESS,
    result: payload.result,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadAgencyPortals(options){
  return (dispatch) => {
    // Set Loading
    dispatch(fetchRequest())

    const promise = api('/admin/agency_portals.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PORTAL_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createAgencyPortal(agencyPortal, options){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      agency_portal: agencyPortal,
    }),
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(createRequest())

    const promise = api(`/admin/agencies/${agencyPortal.agency_id}/agency_portals.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Add to result array
        const resultArray = getState().agencyPortals.result ? getState().agencyPortals.result.slice() : []
        if (!resultArray.includes(json.id)){
          resultArray.push(json.id)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PORTAL)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess({ result: resultArray }))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function generateAgencyPortalAuthToken(agencyPortal, options){
  const config = {
    method: 'PUT',
  }

  return (dispatch) => {
    // Set Loading
    dispatch(updateRequest())

    const { agency_id, id } = agencyPortal
    const promise = api(`/admin/agencies/${agency_id}/agency_portals/${id}/generate_auth_token.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PORTAL)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deleteAgencyPortal(agencyPortal, options){
  const config = {
    method: 'DELETE',
    body: JSON.stringify({
      agency_portal: agencyPortal,
    }),
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(deleteRequest())

    const { agency_id, id } = agencyPortal
    const promise = api(`/admin/agencies/${agency_id}/agency_portals/${id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        // Remove from result array
        const resultArray = getState().agencyPortals.result ? getState().agencyPortals.result.slice() : []
        const index = resultArray.indexOf(json.id)
        if (!resultArray.includes(json.id)){
          resultArray.splice(index, 1)
        }

        const normalizedJson = normalize(json, Schemas.AGENCY_PORTAL)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess({ result: resultArray }))
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return { ...state, loading: true }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        result: action.result,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return { ...state, creating: true }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        result: action.result || state.result,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return { ...state, updating: true }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return { ...state, deleting: true }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        result: action.result || state.result,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
